import {defaultLineup} from "modules/defaultValues/team";
import {createReducer} from "redux-act";
import {
	clearFieldPositionSelection,
	clearMyTeam,
	fetchMyTeamSuccess,
	postMyTeam,
	postMyTeamFailed,
	postMyTeamSuccess,
	postTeamAutoPick,
	postTeamAutoPickFailed,
	postTeamAutoPickSuccess,
	removeFromTeam,
	setFieldSelectedPosition,
	switchPoolType,
	updateLineup,
	selectTurbo,
	swapRider,
	setTradeIdOut,
	setTradeIdIn,
	postMakeTrade,
	postMakeTradeSuccess,
	postMakeTradeFailed,
	clearTrade,
	cancelTrade,
	showRevertModal,
	cancelReverse,
	setTimerState,
	poolRiderModalToggle,
	IPoolRiderModalState,
	getUserTeamSuccess,
	clearUserTeam,
	postMakeTrades,
	showModalTrade,
	hideModalTrade,
} from "modules/actions";
import {findIndex, keys, set} from "lodash";
import {PoolType} from "modules/utils";

export interface ITeamLineup {
	riders: number[];
	constructors: number[];
	squads: number[];
	riders_reserve: number[];
}
export interface ITrade {
	old_rider_id: null | number;
	new_rider_id: null | number;
	old_constructor_id: null | number;
	new_constructor_id: null | number;
	old_squad_id: null | number;
	new_squad_id: null | number;
}
export interface ITeam {
	id: number;
	user_id: number;
	start_event_id: number;
	lineup: ITeamLineup;
	points: number;
	overall_rank: number;
	scoreflow: Record<string, number>;
	turbo_rider: number | null;
	turbo_rider_events: object;
	turbo_rider_free: number;
	value: number;
	salary_cap: number;
	is_playing: boolean;
	event_trades: number;
	trades: ITrade[];
}

export interface IUserTeam {
	lineup: ITeamLineup;
	event_points: number;
	turbo_rider: number | null;
	name: string;
}

export interface ITradeProcess {
	id_in: number;
	id_out: number;
	pool_type: PoolType;
}

export interface ITeamReducer {
	team: ITeam;
	pool_type: PoolType;
	field_selected_path: null | string;
	swap_player: null | number;
	is_saving: boolean;
	is_saved: boolean;
	team_changed: boolean;
	team_updated_after_trade: boolean;
	trade_progress: ITradeProcess[];
	show_revert_modal: boolean;
	timer_ends: boolean;
	pool_rider_modal: IPoolRiderModalState;
	user_team: IUserTeam;
	show_modal_trade: boolean;
}

const defaultState: ITeamReducer = {
	team: {
		id: 0,
		user_id: 0,
		start_event_id: 0,
		lineup: defaultLineup,
		points: 0,
		turbo_rider_free: 3,
		overall_rank: 0,
		scoreflow: {},
		turbo_rider: null,
		turbo_rider_events: {},
		value: 0,
		salary_cap: 0,
		is_playing: false,
		event_trades: 0,
		trades: [],
	},
	pool_type: PoolType.Riders,
	field_selected_path: null,
	swap_player: null,
	is_saving: false,
	is_saved: false,
	team_changed: false,
	team_updated_after_trade: false,
	trade_progress: [
		{
			id_in: 0,
			id_out: 0,
			pool_type: PoolType.Idle,
		},
		{
			id_in: 0,
			id_out: 0,
			pool_type: PoolType.Idle,
		},
	],

	show_revert_modal: false,
	timer_ends: false,
	pool_rider_modal: {
		score: 0,
		event_id: 0,
		show_modal: false,
	},
	user_team: {
		lineup: defaultLineup,
		event_points: 0,
		name: "",
		turbo_rider: null,
	},
	show_modal_trade: false,
};

const is_saving = {
	is_saving: true,
	is_saved: false,
	team_changed: false,
};

const is_saved = {
	is_saving: false,
	is_saved: true,
	team_changed: false,
	team_updated_after_trade: false,
};

const not_saved = {
	is_saving: false,
	is_saved: false,
};

export const team = createReducer<ITeamReducer>({}, defaultState)
	.on(fetchMyTeamSuccess, (state, team) => ({
		...state,
		team,
	}))
	.on(postTeamAutoPick, (state) => ({
		...state,
		...is_saving,
	}))
	.on(postTeamAutoPickSuccess, (state, team) => ({
		...state,
		team,
		field_selected_path: null,
		swap_player: null,
		...is_saved,
	}))
	.on(postTeamAutoPickFailed, (state) => ({
		...state,
		...not_saved,
	}))
	.on(postMyTeam, (state) => ({
		...state,
		...is_saving,
	}))
	.on(postMyTeamSuccess, (state, team) => ({
		...state,
		team,
		field_selected_path: null,
		swap_player: null,
		...is_saved,
	}))
	.on(postMyTeamFailed, (state) => ({
		...state,
		...not_saved,
	}))
	.on(updateLineup, (state, lineup) => ({
		...state,
		team: {
			...state.team,
			lineup,
		},
		field_selected_path: null,
		swap_player: null,
		team_changed: true,
		...not_saved,
	}))
	.on(clearMyTeam, (state) => ({
		...state,
		team: {
			...state.team,
			lineup: defaultState.team.lineup,
			turbo_rider: null,
		},
		field_selected_path: null,
		swap_player: null,
		team_changed: true,
		...not_saved,
	}))
	.on(removeFromTeam, ({team, ...rest}, id) => {
		const lineup = JSON.parse(JSON.stringify(team.lineup));

		keys(lineup).some((position) => {
			const index = lineup[position].indexOf(id);

			if (index !== -1) {
				return set(lineup, `${position}[${index}]`, 0);
			}

			return false;
		});

		return {
			...rest,
			team: {
				...team,
				lineup,
				turbo_rider: team.turbo_rider === id ? null : team.turbo_rider,
			},
			...not_saved,
			swap_player: null,
		};
	})
	.on(switchPoolType, (state, pool_type) => ({
		...state,
		pool_type,
	}))
	.on(setFieldSelectedPosition, (state, payload) => ({
		...state,
		...payload,
		swap_player: null,
	}))
	.on(clearFieldPositionSelection, (state) => ({
		...state,
		field_selected_path: null,
		swap_player: null,
	}))
	.on(selectTurbo, ({team, ...rest}, payload) => ({
		team: {
			...team,
			turbo_rider: team.turbo_rider === payload ? null : payload,
		},
		...rest,
		...not_saved,
		team_changed: true,
	}))
	.on(swapRider, ({swap_player, team, ...rest}, rider_id) => {
		if (!swap_player) {
			return {team, ...rest, swap_player: rider_id};
		}

		if (swap_player === rider_id) {
			return {team, ...rest, swap_player: null};
		}

		const lineup = JSON.parse(JSON.stringify(team.lineup));

		let units_for_swap = 2;
		keys(lineup).some((position) => {
			const index_from = lineup[position].indexOf(swap_player);
			const index_to = lineup[position].indexOf(rider_id);

			if (index_from !== -1) {
				set(lineup, `${position}[${index_from}]`, rider_id);
				units_for_swap--;
			}

			if (index_to !== -1) {
				set(lineup, `${position}[${index_to}]`, swap_player);
				units_for_swap--;
			}

			return !units_for_swap;
		});

		return {
			team: {
				...team,
				lineup,
			},
			...rest,
			team_changed: true,
			swap_player: null,
			...not_saved,
		};
	})
	.on(setTradeIdOut, ({trade_progress, ...rest}, {id, pool_type}) => {
		const is_return_back_index = findIndex(
			trade_progress,
			(trade: ITradeProcess) => id === trade.id_in
		);

		if (is_return_back_index !== -1) {
			return {
				...rest,
				trade_progress: trade_progress.map((trade, index) => {
					return index === is_return_back_index
						? {
								id_in: 0,
								id_out: 0,
								pool_type: PoolType.Idle,
						  }
						: trade;
				}),
			};
		}

		const empty_place_index = findIndex(trade_progress, (trade: ITradeProcess) => {
			if (trade.pool_type && trade.pool_type !== pool_type) {
				return false;
			}
			return 0 === trade.id_out;
		});

		return empty_place_index !== -1
			? {
					...rest,
					trade_progress: trade_progress.map((trade, index) => {
						return index === empty_place_index
							? {
									id_in: trade.id_in,
									id_out: id,
									pool_type,
							  }
							: trade;
					}),
			  }
			: {
					...rest,
					trade_progress,
			  };
	})
	.on(setTradeIdIn, ({trade_progress, ...rest}, {id, pool_type}) => {
		const is_return_back_index = findIndex(
			trade_progress,
			(trade: ITradeProcess) => id === trade.id_out
		);

		if (is_return_back_index !== -1) {
			return {
				...rest,
				trade_progress: trade_progress.map((trade, index) => {
					return index === is_return_back_index
						? {
								id_in: 0,
								id_out: 0,
								pool_type: PoolType.Idle,
						  }
						: trade;
				}),
			};
		}
		const empty_place_index = findIndex(trade_progress, (trade: ITradeProcess) => {
			if (trade.pool_type && trade.pool_type !== pool_type) {
				return false;
			}
			return 0 === trade.id_in;
		});

		return empty_place_index !== -1
			? {
					...rest,
					trade_progress: trade_progress.map((trade, index) => {
						return index === empty_place_index
							? {
									id_in: id,
									id_out: trade.id_out,
									pool_type,
							  }
							: trade;
					}),
			  }
			: {
					...rest,
					trade_progress,
			  };
	})
	.on(postMakeTrade, (state) => ({
		...state,
		...is_saving,
	}))
	.on(postMakeTradeSuccess, (state) => ({
		...state,
		...not_saved,
	}))
	.on(postMakeTradeFailed, (state) => ({
		...state,
		...not_saved,
	}))
	.on(cancelTrade, ({team, trade_progress, ...rest}) => {
		const lineup = JSON.parse(JSON.stringify(team.lineup));

		trade_progress.forEach((trade) => {
			keys(lineup).some((position) => {
				const index = lineup[position].indexOf(trade.id_in);

				if (index !== -1) {
					return set(lineup, `${position}[${index}]`, trade.id_out);
				}
				return false;
			});
		});

		return {
			...rest,
			team: {
				...team,
				lineup,
			},
			trade_progress: [
				{
					id_in: 0,
					id_out: 0,
					pool_type: PoolType.Idle,
				},
				{
					id_in: 0,
					id_out: 0,
					pool_type: PoolType.Idle,
				},
			],
			...not_saved,
			swap_player: null,
		};
	})
	.on(clearTrade, (state) => ({
		...state,
		trade_progress: [
			{
				id_in: 0,
				id_out: 0,
				pool_type: PoolType.Idle,
			},
			{
				id_in: 0,
				id_out: 0,
				pool_type: PoolType.Idle,
			},
		],
	}))
	.on(showRevertModal, (state) => ({
		...state,
		show_revert_modal: true,
	}))
	.on(cancelReverse, (state) => ({
		...state,
		show_revert_modal: false,
	}))
	.on(setTimerState, (state, payload) => ({
		...state,
		timer_ends: payload,
	}))
	.on(poolRiderModalToggle, (state, payload) => ({
		...state,
		pool_rider_modal: payload,
	}))
	.on(getUserTeamSuccess, (state, payload) => {
		const lineup = payload.lineup || defaultLineup;
		return {
			...state,
			user_team: {
				...payload,
				lineup,
			},
		};
	})
	.on(clearUserTeam, (state) => ({
		...state,
		user_team: {
			lineup: defaultLineup,
			event_points: 0,
			name: "",
			turbo_rider: null,
		},
	}))
	.on(postMakeTrades, (state) => ({
		...state,
		team_updated_after_trade: true,
	}))
	.on(showModalTrade, (state) => ({
		...state,
		show_modal_trade: true,
	}))
	.on(hideModalTrade, (state) => ({
		...state,
		show_modal_trade: false,
	}));
